import type { DocumentData } from 'firebase/firestore'
import { ANSWER_TYPES } from '@/lib/constants'
import { mapTranslations } from '@/lib/mappers'
import type {
  AnswerType,
  ConfigQuestion,
  EstateType,
  MaterialEffect,
  QuestionGroup,
  QuestionHint,
  QuestionOption,
  SectionType,
} from '@/lib/types'

/**
 * Document → model for the seven `rule*` collections. Kept in its own file because the rules tree is
 * the largest part of the model; the defaults match the Kotlin readers exactly (a missing enum falls
 * back to NUMBER, a missing order index to 0, a missing string to empty), so a document written by
 * either client reads the same in both.
 */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function order(value: unknown): number {
  return typeof value === 'number' ? value : 0
}

export function parseAnswerType(value: unknown): AnswerType {
  return typeof value === 'string' && (ANSWER_TYPES as readonly string[]).includes(value)
    ? (value as AnswerType)
    : 'NUMBER'
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

export function mapEstateType(id: string, data: DocumentData | undefined): EstateType | null {
  if (typeof data?.name !== 'string') return null
  return {
    id,
    name: data.name,
    nameI18n: mapTranslations(data.nameI18n),
    orderIndex: order(data.orderIndex),
  }
}

export function mapSectionType(id: string, data: DocumentData | undefined): SectionType | null {
  if (typeof data?.name !== 'string') return null
  return {
    id,
    estateTypeId: text(data.estateTypeId),
    name: data.name,
    nameI18n: mapTranslations(data.nameI18n),
    orderIndex: order(data.orderIndex),
    groupIds: stringList(data.groupIds),
  }
}

export function mapGroup(id: string, data: DocumentData | undefined): QuestionGroup | null {
  if (typeof data?.name !== 'string') return null
  return {
    id,
    name: data.name,
    nameI18n: mapTranslations(data.nameI18n),
    orderIndex: order(data.orderIndex),
    isGlobal: data.isGlobal === true,
  }
}

export function mapHint(id: string, data: DocumentData | undefined): QuestionHint | null {
  if (typeof data?.groupId !== 'string') return null
  return {
    id,
    groupId: data.groupId,
    text: text(data.text),
    textI18n: mapTranslations(data.textI18n),
    condition: text(data.condition),
    orderIndex: order(data.orderIndex),
  }
}

export function mapQuestion(id: string, data: DocumentData | undefined): ConfigQuestion | null {
  if (data === undefined) return null
  // A question with neither owner is unusable, but keep it visible so it can be fixed or deleted.
  return {
    id,
    sectionTypeId: text(data.sectionTypeId),
    groupId: text(data.groupId),
    factKey: text(data.factKey),
    prompt: text(data.prompt),
    promptI18n: mapTranslations(data.promptI18n),
    answerType: parseAnswerType(data.answerType),
    unit: text(data.unit),
    materialCategory: text(data.materialCategory),
    materialCategoryId: text(data.materialCategoryId),
    orderIndex: order(data.orderIndex),
    displayCondition: text(data.displayCondition),
  }
}

export function mapOption(id: string, data: DocumentData | undefined): QuestionOption | null {
  if (typeof data?.questionId !== 'string') return null
  return {
    id,
    questionId: data.questionId,
    value: text(data.value),
    label: text(data.label),
    labelI18n: mapTranslations(data.labelI18n),
    orderIndex: order(data.orderIndex),
    // The Kotlin reader defaults a REPEATER field to TEXT, not NUMBER.
    fieldType: typeof data.fieldType === 'string' ? parseAnswerType(data.fieldType) : 'TEXT',
    materialCategory: text(data.materialCategory),
    materialCategoryId: text(data.materialCategoryId),
  }
}

export function mapEffect(id: string, data: DocumentData | undefined): MaterialEffect | null {
  if (typeof data?.questionId !== 'string') return null
  return {
    id,
    questionId: data.questionId,
    materialId: text(data.materialId),
    expression: text(data.expression),
    targetFieldKey: text(data.targetFieldKey),
    fixedMaterial: data.fixedMaterial === true,
  }
}
